import * as React from "react";
import { cn } from "@shared/utils";

/**
 * Textarea component with default styling and forwarded ref.
 *
 * @remarks
 * Applies consistent styles for disabled, focus, and placeholder states.
 * Uses the `cn` utility to merge class names.
 *
 * @param className - Additional CSS classes to apply to the textarea.
 * @param props - All native textarea props.
 * @param ref - Forwarded ref to the underlying HTMLTextAreaElement.
 *
 * @returns A styled textarea React component.
 *
 * @example
 * ```tsx
 * <Textarea placeholder="Enter your message" rows={4} />
 * ```
 */
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

Textarea.displayName = "Textarea";

export { Textarea };
